/**
 * Primitive dell'interfaccia: bottone, chip, avviso, scheda, stato vuoto,
 * scheletro di caricamento, interruttore.
 *
 * Sono scritte a mano sui token di `styles/tema.css` invece di adottare una
 * libreria di componenti. La ragione e' di controllo, non di peso: densita',
 * maschere e chip di stato di questo prodotto sono specifici del dominio, e il
 * doppio tema passa da un solo insieme di variabili CSS.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { StatoAccessibilita, StatoPubblicazione } from "../api/types";
import {
  ETICHETTA_ACCESSIBILITA,
  TONO_ACCESSIBILITA,
} from "../lib/accessibilita";
import { ETICHETTA_STATO } from "../lib/etichette";

/** Tonalita' condivisa da chip, avvisi e indicatori. */
// Il tono vive in `lib/toni.ts`: è dato del sistema di design, non un
// componente, e i moduli di dominio devono poterlo leggere senza tirarsi
// dietro React e i tipi del DOM. Si riesporta perché i componenti che lo
// usano continuino a importarlo da qui, dove lo cercano.
export type { Tono } from "../lib/toni";
import type { Tono } from "../lib/toni";

// --------------------------------------------------------------- bottone -- //

interface ProprietaBottone extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: "primario" | "contorno" | "fantasma" | "critico";
  dimensione?: "normale" | "piccolo" | "largo";
  /** Mostra lo stato di attesa e disabilita il controllo. */
  inCorso?: boolean;
}

export function Bottone({
  variante = "contorno",
  dimensione = "normale",
  inCorso = false,
  disabled,
  className,
  children,
  ...resto
}: ProprietaBottone) {
  const classi = [
    "bottone",
    `bottone--${variante}`,
    dimensione === "piccolo" ? "bottone--piccolo" : "",
    dimensione === "largo" ? "bottone--largo" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={classi}
      disabled={disabled || inCorso}
      aria-busy={inCorso || undefined}
      {...resto}
    >
      {inCorso ? "Attendere…" : children}
    </button>
  );
}

// ------------------------------------------------------------------ chip -- //

export function Chip({
  tono = "neutro",
  pallino = true,
  children,
}: {
  tono?: Tono;
  pallino?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`chip chip--${tono}${pallino ? "" : " chip--senza-pallino"}`}>{children}</span>
  );
}

/**
 * Chip che identifica un nodo dell'alberatura.
 *
 * Porta il percorso completo, non la sola denominazione. Non e' abbondanza: fra
 * i 121 obblighi ci sono due coppie di omonimi perfetti, e per una delle due
 * («Sanzioni per mancata o incompleta comunicazione dei dati», sotto
 * Organizzazione e sotto Personale) coincide anche la sottosezione madre —
 * neppure il solo padre basta a distinguerle. Sui 219 nodi le denominazioni
 * ripetute sono cinquantadue.
 *
 * Il percorso allunga il chip, e la scelta fatta e' di **mandarlo a capo invece
 * che troncarlo**: crescere in altezza dentro una cella di tabella e' molto
 * meno costoso che nascondere la parte identificante. Il contesto e' reso in
 * tono attenuato, cosi' che l'occhio trovi comunque la denominazione, ma resta
 * testo vero: chi legge con lo screen reader sente il percorso intero.
 */
export function ChipNodo({
  antenati,
  denominazione,
  separatore,
}: {
  antenati: string | null;
  denominazione: string;
  /** Separatore dei percorsi, lo stesso di `lib/alberatura`. */
  separatore: string;
}) {
  return (
    <span className="chip chip--neutro chip--senza-pallino chip--percorso">
      {antenati ? (
        <span className="chip__contesto">
          {antenati}
          {separatore}
        </span>
      ) : null}
      {denominazione}
    </span>
  );
}

/** Tono associato a ciascuno stato di pubblicazione. */
const TONO_STATO: Record<StatoPubblicazione, Tono> = {
  bozza: "neutro",
  pubblicato: "positivo",
  archiviato: "attenzione",
};

/** Chip di stato della pubblicazione: bozza · pubblicato · archiviato. */
export function ChipStato({ stato }: { stato: StatoPubblicazione }) {
  return <Chip tono={TONO_STATO[stato]}>{ETICHETTA_STATO[stato]}</Chip>;
}

/**
 * Chip del verdetto di accessibilita' di un documento.
 *
 * Su `non_applicabile` restituisce `null` invece di un chip grigio: su un JSON
 * o un CSV non esiste un controllo da eseguire, e una pillola «non applicabile»
 * accanto a ogni riga di dati aperti riempirebbe la tabella di rumore proprio
 * dove non c'e' niente da fare.
 */
export function ChipAccessibilita({ stato }: { stato: StatoAccessibilita }) {
  if (stato === "non_applicabile") return null;
  return (
    <Chip tono={TONO_ACCESSIBILITA[stato]}>{ETICHETTA_ACCESSIBILITA[stato]}</Chip>
  );
}

// ---------------------------------------------------------------- avviso -- //

const SEGNO_AVVISO: Record<Tono, string> = {
  neutro: "·",
  primario: "i",
  positivo: "✓",
  attenzione: "!",
  grave: "!",
  critico: "✕",
};

export function Avviso({
  tono = "primario",
  titolo,
  children,
}: {
  tono?: Tono;
  titolo?: string;
  children: ReactNode;
}) {
  const variante =
    tono === "primario" || tono === "neutro" ? "informazione" : tono === "positivo" ? "positivo" : tono;
  return (
    <div className={`avviso avviso--${variante}`} role={tono === "critico" ? "alert" : "status"}>
      <span className="avviso__segno" aria-hidden="true">
        {SEGNO_AVVISO[tono]}
      </span>
      <div className="avviso__corpo">
        {titolo ? <div className="avviso__titolo">{titolo}</div> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- scheda -- //

export function Scheda({
  titolo,
  descrizione,
  azioni,
  fitto = false,
  children,
}: {
  titolo?: string;
  descrizione?: string;
  azioni?: ReactNode;
  /** Rimuove il padding del corpo: per tabelle e alberi a tutta larghezza. */
  fitto?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="scheda">
      {titolo || azioni ? (
        <header className="scheda__testata">
          <div>
            {titolo ? <h2 className="scheda__titolo">{titolo}</h2> : null}
            {descrizione ? <p className="campo__aiuto">{descrizione}</p> : null}
          </div>
          {azioni ? <div className="riga">{azioni}</div> : null}
        </header>
      ) : null}
      <div className={`scheda__corpo${fitto ? " scheda__corpo--fitto" : ""}`}>{children}</div>
    </section>
  );
}

// ----------------------------------------------------------- stato vuoto -- //

export function StatoVuoto({
  titolo,
  testo,
  azione,
}: {
  titolo: string;
  testo?: string;
  azione?: ReactNode;
}) {
  return (
    <div className="stato-vuoto">
      <p className="stato-vuoto__titolo">{titolo}</p>
      {testo ? <p className="stato-vuoto__testo">{testo}</p> : null}
      {azione}
    </div>
  );
}

// ------------------------------------------------------------- scheletro -- //

/** Righe grigie animate mostrate mentre la risorsa e' in caricamento. */
export function Scheletro({ righe = 3, altezza = "1.1rem" }: { righe?: number; altezza?: string }) {
  return (
    <div className="pila-stretta" aria-hidden="true">
      {Array.from({ length: righe }, (_, indice) => (
        <div
          key={indice}
          className="scheletro"
          style={{ height: altezza, width: indice === righe - 1 ? "60%" : "100%" }}
        />
      ))}
    </div>
  );
}

/** Stato di attesa annunciato agli screen reader. */
export function InCaricamento({ etichetta = "Caricamento in corso…" }: { etichetta?: string }) {
  return (
    <div role="status" aria-live="polite" style={{ padding: "1.25rem" }}>
      <span className="testo-tenue dato">{etichetta}</span>
      <div style={{ marginTop: "0.75rem" }}>
        <Scheletro />
      </div>
    </div>
  );
}

// ----------------------------------------------------------- interruttore -- //

export function Interruttore({
  acceso,
  onCambia,
  etichetta,
  contesto,
  disabilitato = false,
}: {
  acceso: boolean;
  onCambia: (valore: boolean) => void;
  etichetta: string;
  /**
   * Che cosa comanda questo interruttore, quando l'etichetta visibile si ripete
   * su molte righe: entra nel nome accessibile, dopo l'etichetta — che vi resta
   * per intero, come esige il criterio «etichetta nel nome» (WCAG 2.5.3).
   */
  contesto?: string;
  disabilitato?: boolean;
}) {
  return (
    <label className="interruttore">
      <input
        type="checkbox"
        checked={acceso}
        disabled={disabilitato}
        aria-label={contesto ? `${etichetta} — ${contesto}` : undefined}
        onChange={(evento) => onCambia(evento.target.checked)}
      />
      <span className="interruttore__pista" aria-hidden="true" />
      <span>{etichetta}</span>
    </label>
  );
}
